/**
 * 이상한 방 - 카탈로그 엔트리: 장식 · 가전 그룹
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격 요건)
 * 작성 계약: `src/catalog/entryTypes.ts` · 실측 출처: `public/assets/models.json`
 * 형태 판정 근거: `public/assets/catalog-preview.png` (196종 시각 색인 - 전 항목을 눈으로 확인했다)
 *
 * ## 이 그룹이 맡는 것
 *
 * TV · 컴퓨터 · 라디오 · 스피커 · 조명 · 러그 · 화분 · 박제 · 휴지통 · 책. 담당 모델은 27종인데
 * 엔트리는 12종이다. 나머지 15종을 버린 이유가 이 파일의 실질적인 내용이며, 두 갈래다.
 *
 * 1. **기존 44종이 이미 그 GLB를 쓰고 있다(13종).**
 *    `meshFactory.ts` 의 레시피 표를 그대로 대조한 결과다 -
 *    `television-modern`→텔레비전 · `computer-screen`→모니터 · `computer-keyboard`→키보드 ·
 *    `computer-mouse`→마우스 · `laptop`→노트북 · `speaker`→스피커 · `lamp-round-floor`→플로어램프 ·
 *    `rug-round`→원형 러그 · `rug-rectangle`→사각 러그 · `rug-doormat`→현관매트 ·
 *    `potted-plant`→큰 화분 · `plant-small-1`→화분 · `trashcan`→휴지통.
 *    같은 `modelId` 를 두 엔트리가 쓰면 **화면에 같은 물건이 두 이름으로 나타난다** - 실루엣 중복의 정의다.
 *    id가 겹치지 않아도(`television-modern` ≠ `television`) 화면에 뜨는 물건이 같으면 똑같이 금지다.
 *
 * 2. **실루엣이 이미 있는 엔트리와 겹친다(2종).** `plant-small-2`(기둥 선인장) ·
 *    `plant-small-3`(둥근 관목)는 기존 화분(`plant-small-1`, 원뿔 침엽수)과 화분 형태·크기가 같고
 *    이파리 실루엣만 다르다. 점유 칸이 1×2×1(25cm × 50cm)이라 1인칭 시야에서 셋은 같은 물건으로 읽힌다.
 *    "화분 43개" 문제를 화분 변종 3종으로 푸는 것은 종 수만 늘리고 판독은 그대로 두는 짓이다.
 *
 * ## 이 그룹의 핵심 판단 셋
 *
 * ### (1) 검은 판때기는 절대 바닥에 세우지 않는다
 *
 * TV · 안테나 · 라디오 · 북셸프 스피커 · 책은 전부 `surface` 다. 브라운관 TV를 `floor` 로 두면
 * 0.82m짜리 검은 상자가 방 한가운데 서서 **1인칭 시야를 가리는 monolith** 가 된다. 시야 차단은
 * 이 게임에서 소파·책장 같은 `anchor` 가 의도적으로 지는 역할이고, 소품이 흉내 낼 일이 아니다.
 * 가구 위에만 올라가면 같은 물건이 "책상 위 라디오"라는 맥락까지 얻는다.
 *
 * ### (2) 복도를 채우는 것은 벽과 천장이다 - 이 그룹의 존재 이유
 *
 * 복도는 통로라서 바닥에 뭘 놓으면 못 지나간다. 그래서 지금까지 복도가 텅 비어 있었다.
 * `wall`(벽등 · 곰 박제)과 `ceiling`(실링팬 · 천장등)은 **통로를 한 칸도 먹지 않으면서** 볼 것을 만든다.
 * 이 네 종에 hall 태그를 넉넉히 주고 벽등·천장등의 `weight` 를 7로 올린 것이 그 조치다.
 * 카탈로그 전체에서 `ceiling` 등급은 이 파일이 처음 만든다.
 *
 * ### (3) rotSymmetry는 미리보기를 보고 정했다 - 특히 러그와 실링팬
 *
 *  - `rug-square` 는 정사각 필드에 동심 정사각 테두리라 90° 회전이 화면에 아무 흔적도 남기지 않는다 → 4
 *  - `rug-rounded` 는 양끝이 반원인 스타디움 형태 → 180°는 무변화, 90°는 가로세로가 바뀌어 확연하다 → 2
 *  - `ceiling-fan` 은 **날개가 3장**이다. 3회 대칭은 1·2·4 어디에도 없지만, 90° 격자 위에서는
 *    네 방향이 전부 다르게 보이므로 → 1. (여기서 4를 선언하면 술래가 찾을 수 없는 정답이 생긴다)
 *
 * ## recolorSlot 배분 규칙
 *
 * GLB의 baseColorFactor를 실제로 읽어 **면적을 지배하는 색면 하나**만 지목했다.
 * 캐비닛이 있는 가전(브라운관 TV · 라디오 · 북셸프 스피커)은 `wood`(몸통)만 칠해 화면·다이얼을
 * 검게 남긴다 - 전 슬롯을 칠하면 화면이 사라져 "TV처럼 생긴 색덩어리"가 된다.
 * 조명은 갓(`lamp`)을, 러그는 안쪽 필드(`carpet`)를, 박제는 털(`fur`)을 지목한다.
 */

import type { CatalogEntryDraft } from '../entryTypes'
import { cellsFromMeters } from '../entryTypes'

/* ------------------------------------------------------------------------------------------------
 * 실측 크기 (public/assets/models.json 의 sizeMeters 전사)
 * ---------------------------------------------------------------------------------------------- */

/**
 * 채택한 12종의 실측 바운딩 크기(m). **매니페스트에서 그대로 옮긴 값이며 손 추정치가 하나도 없다.**
 *
 * 셀 변환을 `cellsFromMeters()` 에 맡기는 이유는 `entryTypes.ts` 가 설명한 그대로다 -
 * 스케일이 반영된 실측값을 손으로 다시 나누면 y(높이)와 z(깊이)를 바꿔 적는 사고가 반드시 나고,
 * 그 결과는 눕혀진 모델이 아니라 **점유 칸 오류**다.
 *
 * 축 순서는 `Cell3` 규약을 따른다 - **y가 높이**, x·z가 바닥 평면.
 */
const MEASURED_SIZE_METERS = {
  'television-vintage': { x: 0.82, y: 0.54, z: 0.54 },
  'television-antenna': { x: 0.529, y: 0.205, z: 0.16 },
  radio: { x: 0.63, y: 0.456, z: 0.195 },
  'speaker-small': { x: 0.296, y: 0.596, z: 0.266 },
  'ceiling-fan': { x: 0.909, y: 0.267, z: 1.05 },
  'lamp-square-ceiling': { x: 0.24, y: 0.46, z: 0.24 },
  'lamp-square-floor': { x: 0.24, y: 1.72, z: 0.24 },
  'lamp-wall': { x: 0.453, y: 0.186, z: 0.301 },
  'rug-rounded': { x: 3.14, y: 0.02, z: 1.84 },
  'rug-square': { x: 1.809, y: 0.02, z: 1.84 },
  bear: { x: 0.779, y: 0.9, z: 0.495 },
  books: { x: 0.301, y: 0.208, z: 0.189 },
} as const

/** 위 표에 실측이 등록된 모델만 이 파일에서 엔트리로 만들 수 있다. */
type DecorModelId = keyof typeof MEASURED_SIZE_METERS

/**
 * `footprint` 를 뺀 엔트리 초안. footprint는 손으로 적는 필드가 아니라 **modelId에서 파생되는 값**이므로
 * 아예 입력에서 제거했다. 둘을 나란히 적게 두면 언젠가 한쪽만 고쳐져 어긋난다.
 */
type DecorDraftSpec = Omit<CatalogEntryDraft, 'footprint' | 'modelId'> &
  Readonly<{ modelId: DecorModelId }>

/** 실측 표에서 점유 칸을 파생시켜 엔트리를 완성한다. modelId ↔ footprint 불일치를 타입으로 막는 장치다. */
function draft(spec: DecorDraftSpec): CatalogEntryDraft {
  return { ...spec, footprint: cellsFromMeters(MEASURED_SIZE_METERS[spec.modelId]) }
}

/* ------------------------------------------------------------------------------------------------
 * 엔트리 목록
 * ---------------------------------------------------------------------------------------------- */

/**
 * 장식 · 가전 그룹 12종.
 *
 * `weight` 분포의 의도: 벽등 · 천장등 · 책무더기처럼 **집 안 어디에나 있는** 물건을 6~7로 올리고,
 * 곰 박제 · TV 안테나처럼 한 집에 하나 있을까 말까 한 물건을 1~2로 눌렀다.
 * 종을 늘려도 가중치가 평평하면 아파트에 실링팬이 아홉 개 돈다 - 밀도는 종 수가 아니라 이 분포가 만든다.
 */
export const DECOR_ELECTRONICS_ENTRIES: readonly CatalogEntryDraft[] = [
  /* --- 가전: 전부 surface. 검은 판때기를 바닥에 세우지 않는다는 규칙의 적용 대상 ---------------- */

  // 목재 캐비닛 + 전면 브라운관. 기존 `television`(television-modern, 얇은 평면 패널)과는
  // 두께가 3칸 대 2칸이고 나무 상자 대 검은 판이라 정면·측면 실루엣이 모두 갈린다.
  // rot 1: 화면이 한쪽 면에만 있어 네 방향이 전부 구별된다.
  // recolor wood: 캐비닛(면적 지배)만 칠하고 화면(metalDark)·유리(metal)는 남겨 "TV임"을 유지한다.
  draft({
    id: 'television-vintage',
    modelId: 'television-vintage',
    name: '브라운관 TV',
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'wood',
    tags: ['living', 'bedroom'],
    weight: 3,
    defaultColorId: 5,
  }),

  // 토끼귀 안테나. 두 로드의 길이·각도가 서로 달라(미리보기 확인) 180°도 확연히 다르다.
  // rot 1. sizeClass surface: TV장·브라운관 TV 위에 얹히는 물건이라 바닥에 서면 정체를 잃는다.
  // recolor metalMedium: 슬롯이 이것 하나뿐이다.
  draft({
    id: 'television-antenna',
    modelId: 'television-antenna',
    name: 'TV 안테나',
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'metalMedium',
    tags: ['living', 'bedroom', 'hall'],
    weight: 2,
    defaultColorId: 9,
  }),

  // 손잡이 달린 휴대용 라디오. 전면에만 다이얼과 노브가 있다 → rot 1.
  // recolor wood: 몸통이 색면을 지배하고, 전면 패널(metalMedium)과 손잡이(metal)는 남긴다.
  // hall 태그: 복도 선반 위 잡동사니로 자연스럽고, 복도 소품 풀을 늘려야 한다.
  draft({
    id: 'radio',
    modelId: 'radio',
    name: '라디오',
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'wood',
    tags: ['living', 'kitchen', 'bedroom', 'hall'],
    weight: 4,
    defaultColorId: 5,
  }),

  // 북셸프 스피커. 기존 `speaker`(같은 키트의 타워형, 2×6×2)의 절반 높이라 실루엣이 갈린다.
  // rot 1: 유닛이 박힌 전면 배플이 한쪽에만 있다. 바닥 면적이 정사각(2×2)이어도 네 방향이 다르다.
  // recolor wood: 캐비닛만. 배플(metalMedium)까지 칠하면 유닛 두 개가 뭉개져 스피커로 안 읽힌다.
  draft({
    id: 'speaker-small',
    modelId: 'speaker-small',
    name: '북셸프 스피커',
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'wood',
    tags: ['living', 'bedroom', 'hall'],
    weight: 5,
    defaultColorId: 5,
  }),

  // 책 세 권이 나란히 서고 한 권이 기울어 있다 → 어느 방향에서도 같아 보이지 않는다(rot 1).
  // 기존 `book`(1×1×1 단권)과는 점유 칸도 형태도 다르다.
  // recolor carpetDarker: 가장 큰 표지. 나머지 표지(carpetWhite·plant)를 남겨야 "여러 권"으로 읽힌다.
  draft({
    id: 'book-stack',
    modelId: 'books',
    name: '책 무더기',
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'carpetDarker',
    tags: ['living', 'bedroom', 'kitchen', 'hall'],
    weight: 7,
    defaultColorId: 1,
  }),

  /* --- 천장: 통로를 한 칸도 먹지 않으면서 복도에 볼 것을 만든다. 카탈로그 최초의 ceiling 등급 ------ */

  // 날개 3장. 3회 대칭은 1·2·4 어디에도 없고, 90° 격자 위에서는 네 방향이 전부 다르다 → rot 1.
  // (footprint가 4×5로 정사각이 아니라 애초에 4를 선언할 수도 없다.)
  // recolor wood: 날개가 유일하게 넓은 색면이다. 모터 하우징(metalLight)·전구(lamp)는 남긴다.
  draft({
    id: 'ceiling-fan',
    modelId: 'ceiling-fan',
    name: '실링팬',
    rotSymmetry: 1,
    sizeClass: 'ceiling',
    recolorSlot: 'wood',
    tags: ['living', 'bedroom', 'kitchen', 'hall'],
    weight: 4,
    defaultColorId: 8,
  }),

  // 정사각 갓이 수직축 대칭이라 90° 회전이 화면에 흔적을 남기지 않는다 → rot 4 (점유 칸도 1×1 정사각).
  // recolor lamp: 갓이 물건의 전부다. 매다는 금속(metal)은 면적이 없다.
  // weight 7 + 전 구획 태그: 천장등은 방마다 하나씩 있는 물건이고, 복도 밀도의 가장 값싼 해답이다.
  draft({
    id: 'lamp-ceiling-square',
    modelId: 'lamp-square-ceiling',
    name: '천장등',
    rotSymmetry: 4,
    sizeClass: 'ceiling',
    recolorSlot: 'lamp',
    tags: ['hall', 'living', 'bedroom', 'kitchen', 'bath'],
    weight: 7,
    defaultColorId: 9,
  }),

  /* --- 벽: 복도 밀도의 나머지 절반 ------------------------------------------------------------- */

  // 벽에 붙는 등판 + 바깥 위로 열린 쐐기형 갓. 벽 쪽과 열린 쪽이 다르므로 rot 1
  // (점유 칸이 2×2 정사각이라 형태만 보고 4를 줄 뻔한 자리다 - 미리보기가 아니면 틀렸을 판정).
  // recolor lamp: 갓이 색면을 지배한다. weight 7: 복도·욕실 벽을 채울 주력이다.
  draft({
    id: 'lamp-wall',
    modelId: 'lamp-wall',
    name: '벽등',
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'lamp',
    tags: ['hall', 'living', 'bedroom', 'bath', 'kitchen'],
    weight: 7,
    defaultColorId: 9,
  }),

  // 벽걸이 판 위의 곰 머리 박제(미리보기 확인 - 곰 인형이 아니다). 얼굴이 한쪽만 본다 → rot 1.
  // recolor fur: 털이 색면의 전부. 판(wood)과 눈코(metalDark)는 남겨야 얼굴로 읽힌다.
  // weight 1: 한 집에 하나면 충분하고, 복도에서 한 번 마주치는 것이 이 물건의 값어치다.
  draft({
    id: 'bear-trophy',
    modelId: 'bear',
    name: '곰 머리 박제',
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'fur',
    tags: ['hall', 'living', 'bedroom'],
    weight: 1,
    defaultColorId: 5,
  }),

  /* --- 바닥 조명 · 러그 ------------------------------------------------------------------------ */

  // 정사각 단면의 기둥형 플로어램프. 기존 `lamp-floor`(lamp-round-floor)는 가느다란 봉 + 원형 갓이라
  // 실루엣이 "기둥 대 봉"으로 갈리고 점유 칸도 1×7×1 대 2×7×2로 다르다.
  // rot 4: 정사각 기둥이라 90° 회전이 무변화다.
  draft({
    id: 'lamp-floor-square',
    modelId: 'lamp-square-floor',
    name: '기둥 플로어램프',
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'lamp',
    tags: ['living', 'bedroom', 'hall'],
    weight: 3,
    defaultColorId: 8,
  }),

  // 정사각 필드 + 동심 정사각 테두리 → 90° 회전이 화면에 아무 흔적도 남기지 않는다(rot 4).
  // 기존 `rug-round`(원형)와는 모서리 유무로, `rug-rectangle`(직사각)과는 비율로 갈린다.
  // recolor carpet: 안쪽 필드만. 테두리(carpetDarker)를 남겨야 러그의 무늬가 살아 있다.
  draft({
    id: 'rug-square',
    modelId: 'rug-square',
    name: '정사각 러그',
    rotSymmetry: 4,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['living', 'bedroom', 'hall'],
    weight: 3,
    defaultColorId: 2,
  }),

  // 양끝이 반원인 스타디움형 러그. 180°는 무변화, 90°는 가로세로가 바뀌어 확연하다 → rot 2.
  // 기존 `rug-rectangle`과 점유 칸(13×1×8)이 같지만 각진 모서리 대 둥근 끝이라 바닥에서 확연히 다르다.
  // ※ 통합 담당 참고: catalog.test.ts의 footprint 유일성 검사와 충돌한다(파일 말미 주석 참조).
  draft({
    id: 'rug-oval',
    modelId: 'rug-rounded',
    name: '타원 러그',
    rotSymmetry: 2,
    sizeClass: 'floor',
    recolorSlot: 'carpet',
    tags: ['living', 'bedroom', 'hall'],
    weight: 3,
    defaultColorId: 7,
  }),
]

/* ------------------------------------------------------------------------------------------------
 * 통합 담당에게 남기는 메모 - footprint 유일성 검사
 * ---------------------------------------------------------------------------------------------- */

/**
 * `catalog.test.ts` 의 "최소 크기 소품을 제외하면 footprint가 서로 다르다" 검사는 44종 시절의 규칙이다.
 * 카탈로그가 120종을 넘는 순간 이 검사는 **실루엣이 아니라 정수 세 개의 충돌**을 잡게 되고,
 * 그때 잘려 나가는 것은 비슷한 물건이 아니라 우연히 치수가 겹친 전혀 다른 물건이다.
 *
 * 이 파일이 만드는 충돌은 셋이며, 전부 **실측값이라 조정할 수 없다**(손으로 고치는 순간 점유 칸이 거짓이 된다):
 *
 *  | 이 파일 | 점유 칸 | 충돌 상대 | 실제로 구별되는가 |
 *  |---------|---------|-----------|-------------------|
 *  | `television-antenna` | 3×1×1 | 기존 `keyboard` | 벌어진 로드 두 개 대 평평한 판 - 확연히 다르다 |
 *  | `speaker-small`      | 2×3×2 | 기존 `lamp-table` | 나무 상자 대 원뿔 갓 - 확연히 다르다 |
 *  | `rug-oval`           | 13×1×8 | 기존 `rug-rectangle` | 둥근 끝 대 각진 모서리 - 바닥에서 확연히 다르다 |
 *
 * 셋 다 남길 값어치가 있다고 판단해 넣었다. 검사를 "점유 칸 + modelId" 쌍의 유일성으로 바꾸거나
 * (같은 GLB를 두 번 쓰는 것이야말로 이 검사가 원래 막으려던 것이다) 최소 크기 예외를 넓히는 것을 권한다.
 * 반대로 검사를 그대로 두기로 정했다면 `rug-oval` 하나만 빼도 나머지 둘은 다른 그룹과의 조합 문제로 남는다.
 *
 * (위 표가 이 판단의 기록 전부다. 같은 내용을 담은 배열 export가 있었지만 참조하는 곳이 없어 지웠다.)
 */
